import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Clock, ShieldCheck, RefreshCcw } from 'lucide-react';
import PublicNavbar from '../components/PublicNavbar';
import BrandTitle from '../components/BrandTitle';

const Refund = () => {
  useEffect(() => {
    document.title = 'Refund Policy | CARTIEAI';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="refund-page">
      <PublicNavbar />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-16">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white mb-8" data-testid="refund-back-link">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="mb-12">
          <p className="text-[11px] font-bold tracking-[0.25em] text-emerald-300 mb-3">LEGAL · LAST UPDATED MAY 2026</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4"><BrandTitle>Refund Policy</BrandTitle></h1>
          <p className="text-lg text-slate-300">
            We want every customer to feel confident in their decision to use CARTIEAI. This policy explains exactly when and how you can request a refund.
          </p>
        </div>

        {/* Key promises */}
        <div className="grid sm:grid-cols-3 gap-3 mb-12">
          <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/15 p-5">
            <Clock className="w-5 h-5 text-emerald-300 mb-2" />
            <p className="text-sm font-semibold mb-1">14-day full refund</p>
            <p className="text-xs text-slate-300">For all paid annual plans</p>
          </div>
          <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/15 p-5">
            <RefreshCcw className="w-5 h-5 text-emerald-300 mb-2" />
            <p className="text-sm font-semibold mb-1">Cancel anytime</p>
            <p className="text-xs text-slate-300">Monthly plans pro-rated</p>
          </div>
          <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/15 p-5">
            <ShieldCheck className="w-5 h-5 text-emerald-300 mb-2" />
            <p className="text-sm font-semibold mb-1">No questions asked</p>
            <p className="text-xs text-slate-300">Within the first 14 days</p>
          </div>
        </div>

        <div className="prose prose-invert prose-slate max-w-none prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-p:text-slate-200 prose-p:leading-relaxed prose-li:text-slate-200 prose-strong:text-white prose-a:text-emerald-300 prose-a:no-underline hover:prose-a:underline">

          <h2>1. The Promise</h2>
          <p>
            We stand behind CARTIEAI. If you're not getting clear, measurable value from the platform within the first <strong>14 days</strong> of any paid plan, we'll refund you in full — no friction, no exit interview.
          </p>

          <h2>2. Eligibility</h2>
          <p>You may request a full refund if:</p>
          <ul>
            <li>You purchased a paid plan (Starter, Pro, or Enterprise) within the last 14 days</li>
            <li>You have not exceeded our standard fair-use thresholds (e.g. running automated audit workloads against more than 100 distinct cloud accounts within the trial window)</li>
            <li>The request is submitted via email to <a href="mailto:hello@cartieai.com">hello@cartieai.com</a> with your original receipt or account email</li>
          </ul>

          <h2>3. What's Not Refundable</h2>
          <ul>
            <li>One-time professional services or implementation fees once work has begun</li>
            <li>Custom enterprise contracts where a different refund clause was negotiated and signed</li>
            <li>Subscription periods beyond 14 days from the original purchase or renewal date (you can still cancel — see Section 4)</li>
          </ul>

          <h2>4. Cancellations Outside the Refund Window</h2>
          <p>
            You can cancel any subscription at any time from your <Link to="/settings">account settings</Link> or by emailing us. Your access continues until the end of your current billing period — you will not be charged again. Annual plans are not refunded pro-rata after the 14-day window, but no further charges will occur.
          </p>

          <h2>5. How to Request a Refund</h2>
          <p>Email <a href="mailto:hello@cartieai.com">hello@cartieai.com</a> with the subject line <em>"Refund request — [your account email]"</em>. We aim to respond within one business day and process the refund within five business days. Refunds are returned to the original payment method.</p>

          <h2>6. Disputes &amp; Chargebacks</h2>
          <p>
            If something feels off, please reach out to us first. A direct chargeback through your bank typically takes longer than a refund from us and adds unnecessary friction for both sides. We've never had a chargeback we couldn't have resolved in a 5-minute email.
          </p>

          <h2>7. Changes to This Policy</h2>
          <p>
            We may update this policy as our business evolves. The version that applies to your purchase is the version in effect at the time of payment. We'll never apply a stricter policy retroactively.
          </p>

          <h2>8. Questions?</h2>
          <p>
            We're a small, founder-led team. The fastest way to get an answer is to email <a href="mailto:hello@cartieai.com">hello@cartieai.com</a> — Lakshmi (founder) reads and replies personally to every refund request.
          </p>
        </div>

        {/* Contact card */}
        <div className="mt-12 rounded-2xl bg-white/[0.02] border border-white/5 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center flex-shrink-0">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-white">Need a refund or have a question?</p>
            <p className="text-sm text-slate-300">Email <a href="mailto:hello@cartieai.com" className="text-emerald-300 hover:underline">hello@cartieai.com</a> — Lakshmi reads every message.</p>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/5 py-6 px-4 text-center text-xs text-slate-300">
        &copy; 2026 CARTIEAI ·{' '}
        <Link to="/privacy-policy" className="hover:text-slate-200">Privacy</Link>{' · '}
        <Link to="/terms" className="hover:text-slate-200">Terms</Link>{' · '}
        <Link to="/contact" className="hover:text-slate-200">Contact</Link>
      </footer>
    </div>
  );
};

export default Refund;
